import React, { useMemo } from 'react';
import { cn } from '../../lib/utils';

/**
 * AgentGraph — live visualisation of the committee run.
 *
 * Each node represents one agent. Status is derived from the SSE event stream
 * already plumbed into CommitteeRunPage:
 *   - 'idle'      — gray, dashed border
 *   - 'running'   — pulse + sky border
 *   - 'complete'  — solid emerald border + check
 *   - 'error'     — solid red border + cross
 *
 * Props:
 *   statuses  — { [role: string]: 'idle'|'running'|'complete'|'error' }
 *   verdicts  — optional { [role]: 'BUY'|'HOLD'|... } drawn under the node label
 *   compact   — boolean. When true, renders smaller for sidebars.
 */
export default function AgentGraph({ statuses = {}, verdicts = {}, compact = false }) {
  const phases = useMemo(() => PHASES, []);

  return (
    <div className={cn('w-full overflow-x-auto', compact ? 'text-[11px]' : 'text-xs')}>
      <div className="flex items-stretch gap-4 min-w-max p-3">
        {phases.map((phase, pIdx) => (
          <React.Fragment key={phase.id}>
            <div className="flex flex-col items-center">
              <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {phase.label}
              </div>
              <div className={cn('flex gap-2', phase.nodes.length > 1 ? 'flex-col' : '')}>
                {phase.nodes.map((node) => {
                  const status = statuses[node.role] || 'idle';
                  const verdict = verdicts[node.role];
                  return (
                    <div
                      key={node.role}
                      className={cn(
                        'flex items-center gap-2 rounded border px-3 py-2 transition-colors',
                        compact ? 'min-w-[120px]' : 'min-w-[140px]',
                        statusBorder(status),
                        status === 'running' && 'animate-pulse',
                      )}
                      title={`${node.label} — ${status}`}
                    >
                      <StatusDot status={status} />
                      <div className="flex flex-col">
                        <div className="font-medium text-foreground">{node.label}</div>
                        {verdict ? (
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            {verdict}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {pIdx < phases.length - 1 && (
              <div className="flex items-center text-muted-foreground/40 text-xl select-none" aria-hidden>
                →
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const color =
    status === 'complete' ? 'bg-emerald-500' :
    status === 'running'  ? 'bg-sky-500'     :
    status === 'error'    ? 'bg-red-500'     :
    'bg-muted-foreground/30';
  return <span className={cn('h-2 w-2 rounded-full', color)} aria-hidden />;
}

function statusBorder(status) {
  switch (status) {
    case 'complete': return 'border-emerald-500/40 bg-emerald-500/5';
    case 'running':  return 'border-sky-500/60 bg-sky-500/5';
    case 'error':    return 'border-red-500/50 bg-red-500/5';
    default:         return 'border-dashed border-muted-foreground/30 bg-muted/20';
  }
}

const PHASES = [
  {
    id: 'analysts',
    label: 'Analysts',
    nodes: [
      { role: 'fundamentals',   label: 'Fundamentals (Mira)' },
      { role: 'technical',      label: 'Technical' },
      { role: 'ownership',      label: 'Ownership (Arjun)' },
      { role: 'macro_sector',   label: 'Macro / Sector (Arjun)' },
      { role: 'news_filings',   label: 'News + Filings' },
      { role: 'sentiment_flow', label: 'Sentiment + Flow' },
    ],
  },
  {
    id: 'debate',
    label: 'Debate',
    nodes: [
      { role: 'bull', label: 'Bull' },
      { role: 'bear', label: 'Bear' },
    ],
  },
  {
    id: 'trader',
    label: 'Trader',
    nodes: [{ role: 'trader', label: 'Trader' }],
  },
  {
    id: 'risk',
    label: 'Risk Panel',
    nodes: [
      { role: 'risk_aggressive',   label: 'Aggressive' },
      { role: 'risk_neutral',      label: 'Neutral' },
      { role: 'risk_conservative', label: 'Conservative (Kavya)' },
      { role: 'risk_synthesizer',  label: 'Synthesiser' },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    nodes: [{ role: 'compliance', label: 'Compliance' }],
  },
  {
    id: 'cio',
    label: 'CIO',
    nodes: [{ role: 'cio', label: 'CIO Synthesis' }],
  },
];

/**
 * Helper to derive a `statuses` map from the SSE event log already kept on
 * CommitteeRunPage. Pass `events` (array of SSE objects) and the current
 * `decision`; returns { [role]: status, _verdicts: {} }.
 */
export function deriveAgentState(events, decision) {
  const statuses = {};
  const verdicts = {};
  for (const e of events || []) {
    const role = e.role;
    if (!role) continue;
    if (e.type === 'agent.start' || e.type === 'agent.running') {
      statuses[role] = 'running';
    } else if (e.type === 'agent.complete') {
      statuses[role] = 'complete';
      if (e.verdict) verdicts[role] = e.verdict;
    } else if (e.type === 'agent.error') {
      statuses[role] = 'error';
    }
  }
  if (decision && decision.verdict) {
    statuses.cio = statuses.cio || 'complete';
    verdicts.cio = decision.verdict;
  }
  return { statuses, verdicts };
}
